import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GlobalSettingsContext = createContext(null);

export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
];

export const CURRENCIES = [
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
  { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
];

export const COUNTRIES = [
  'United Kingdom', 'United States', 'Canada', 'Australia', 'Germany',
  'France', 'Spain', 'Italy', 'Portugal', 'Netherlands', 'Belgium',
  'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Ireland', 'Poland', 'Czech Republic', 'India', 'Japan', 'South Korea',
  'Singapore', 'Hong Kong', 'Taiwan', 'China', 'Thailand', 'Vietnam',
  'Philippines', 'Indonesia', 'Malaysia', 'Brazil', 'Mexico', 'Argentina',
  'Colombia', 'Chile', 'Peru', 'United Arab Emirates', 'Saudi Arabia',
  'Qatar', 'Kuwait', 'Israel', 'Turkey', 'South Africa', 'Nigeria',
  'Kenya', 'Egypt', 'Morocco', 'New Zealand', 'Russia',
];

export const TIMEZONES = Intl.supportedValuesOf
  ? Intl.supportedValuesOf('timeZone')
  : [
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
    'Europe/Rome', 'Europe/Lisbon', 'America/New_York', 'America/Chicago',
    'America/Denver', 'America/Los_Angeles', 'America/Toronto',
    'America/Vancouver', 'Australia/Sydney', 'Australia/Melbourne',
    'Asia/Tokyo', 'Asia/Singapore', 'Asia/Kolkata', 'Asia/Dubai',
    'Pacific/Auckland', 'Africa/Lagos', 'Africa/Nairobi',
  ];

const LANG_COUNTRY_MAP = {
  en: { currency: 'GBP', country: 'United Kingdom' },
  es: { currency: 'EUR', country: 'Spain' },
  fr: { currency: 'EUR', country: 'France' },
  de: { currency: 'EUR', country: 'Germany' },
  pt: { currency: 'EUR', country: 'Portugal' },
  it: { currency: 'EUR', country: 'Italy' },
};

function detectBrowserLanguage() {
  const nav = navigator.language || navigator.userLanguage || 'en';
  const code = nav.split('-')[0].toLowerCase();
  return LANGUAGES.find(l => l.code === code) ? code : 'en';
}

function detectBrowserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'Europe/London'; }
}

export function GlobalSettingsProvider({ children }) {
  const [language, setLanguage] = useState(() => localStorage.getItem('hh_language') || detectBrowserLanguage());
  const [currency, setCurrency] = useState(() => localStorage.getItem('hh_currency') || 'GBP');
  const [country, setCountry] = useState(() => localStorage.getItem('hh_country') || 'United Kingdom');
  const [region, setRegion] = useState(() => localStorage.getItem('hh_region') || '');
  const [city, setCity] = useState(() => localStorage.getItem('hh_city') || '');
  const [timezone, setTimezone] = useState(() => localStorage.getItem('hh_timezone') || detectBrowserTimezone());
  const [detectedCountry, setDetectedCountry] = useState('');
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('hh_language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = 'ltr';
  }, [language]);
  useEffect(() => { localStorage.setItem('hh_currency', currency); }, [currency]);
  useEffect(() => { if (country) localStorage.setItem('hh_country', country); }, [country]);
  useEffect(() => { if (region) localStorage.setItem('hh_region', region); }, [region]);
  useEffect(() => { if (city) localStorage.setItem('hh_city', city); }, [city]);
  useEffect(() => { if (timezone) localStorage.setItem('hh_timezone', timezone); }, [timezone]);

  // Load preferences from backend (called after login)
  const loadPreferencesFromBackend = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/preferences`, { withCredentials: true });
      if (data.language) setLanguage(data.language);
      if (data.currency) setCurrency(data.currency);
      if (data.country) setCountry(data.country);
      if (data.region) setRegion(data.region);
      if (data.city) setCity(data.city);
      if (data.timezone) setTimezone(data.timezone);
      setPrefsLoaded(true);
    } catch {
      // Not logged in or preferences not set — use local defaults
      setPrefsLoaded(true);
    }
  }, []);

  // Save preferences to backend
  const savePreferencesToBackend = useCallback(async () => {
    try {
      await axios.put(`${API}/auth/preferences`, {
        language, currency, country, region, city, timezone,
      }, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  }, [language, currency, country, region, city, timezone]);

  // Auto-load from backend on mount (only when there's a session cookie)
  useEffect(() => {
    // Only attempt to load preferences if there's a session cookie
    const hasCookie = document.cookie.includes('session_token');
    if (hasCookie) {
      loadPreferencesFromBackend();
    } else {
      setPrefsLoaded(true);
    }
  }, [loadPreferencesFromBackend]);

  const applyLocaleDefaults = (langCode) => {
    const defaults = LANG_COUNTRY_MAP[langCode];
    if (defaults) {
      if (!localStorage.getItem('hh_currency_manual')) setCurrency(defaults.currency);
      if (!localStorage.getItem('hh_country') && defaults.country) setCountry(defaults.country);
    }
  };

  const setLanguageWithDefaults = (langCode) => {
    setLanguage(langCode);
    applyLocaleDefaults(langCode);
  };

  const setCurrencyManual = (code) => {
    setCurrency(code);
    localStorage.setItem('hh_currency_manual', 'true');
  };

  return (
    <GlobalSettingsContext.Provider value={{
      language, setLanguage: setLanguageWithDefaults,
      currency, setCurrency: setCurrencyManual,
      country, setCountry,
      region, setRegion,
      city, setCity,
      timezone, setTimezone,
      detectedCountry, setDetectedCountry,
      prefsLoaded,
      loadPreferencesFromBackend,
      savePreferencesToBackend,
      LANGUAGES, CURRENCIES, COUNTRIES, TIMEZONES,
    }}>
      {children}
    </GlobalSettingsContext.Provider>
  );
}

export function useGlobalSettings() {
  const ctx = useContext(GlobalSettingsContext);
  if (!ctx) throw new Error('useGlobalSettings must be used within GlobalSettingsProvider');
  return ctx;
}
